import React from 'react'
import {
  AdCreative,
  AdCreativeStatus,
  AssetApprovalStatus,
  PublishCampaignDTO,
  resolveOptimizationDefinition,
} from '@marketing-milk/interfaces'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { hasImproperCTA, isValidCreative } from '../../../campaigns/helpers/campaign.helpers'
import { AdCreativeList } from './'

// Facebook rejects the 51st ad in an ad set (error #1487809), and paused ads still count
// toward it. Because every selected creative is attached to every selected ad set, this
// selection count IS the ad count per ad set — so Meta's limit is our limit exactly.
// The previous cap of 26 was the alphabet (see creativeLetter), not a platform rule.
const MAX_SELECTED_CREATIVES = 50

/**
 * A client's rejection withholds the ad from every campaign that picks its ads here —
 * the standalone campaign form and the sprint draft's Choose Ads modal both come through
 * this step. Deliberately not folded into `isValidCreative`: that predicate also decides
 * what the picker lists at all, and a rejected ad has to stay visible. An ad the AE
 * can't find reads as one that vanished.
 */
const withholdRejected = (creative: AdCreative) =>
  creative.reviewStatus === AssetApprovalStatus.Rejected ? 'Rejected by client' : undefined

type Props = {
  creatives?: AdCreative[]
  selectedCreatives: AdCreative[]
  campaignDto: PublishCampaignDTO
  onChangeCreatives: (creatives: AdCreative[]) => void
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isFetching?: boolean
  skipCreativeValidation?: boolean
  /** The sprint's review surface, when this picker is editing a sprint's draft campaign. */
  reviewHref?: string
  toolbarExtra?: React.ReactNode
}

export function AdCreativeStep({
  campaignDto,
  selectedCreatives,
  onChangeCreatives,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  skipCreativeValidation,
  reviewHref,
  toolbarExtra,
  ...props
}: Props) {
  const [creatives, setCreatives] = useState(props.creatives)

  // Keyed on which ads are attached rather than on the array itself: callers rebuild
  // `selectedCreatives` from the campaign's groups on every render, so a reference
  // dependency would re-filter forever.
  const attachedKey = selectedCreatives.map(c => c.id).join(',')

  useEffect(() => {
    if (!props.creatives) return
    setCreatives(filterCreatives(props.creatives))
  }, [props.creatives, attachedKey])

  const filterCreatives = (creatives: AdCreative[]) => {
    if (skipCreativeValidation) return creatives
    const optimization = resolveOptimizationDefinition(campaignDto.campaign)
    // Ads are also attached to a campaign group when a sprint is created, before they have
    // any content, so an attached ad can fail every check below. Filtering it out would
    // leave it on the campaign with nothing on this screen to take it off.
    const attached = new Set(selectedCreatives.map(c => c.id))
    return creatives.filter(
      creative => attached.has(creative.id) || isSelectable(creative, optimization)
    )
  }

  const isSelectable = (
    creative: AdCreative,
    optimization: ReturnType<typeof resolveOptimizationDefinition>
  ) =>
    creative.status !== AdCreativeStatus.DELETED && // not deleted
    creative.status !== AdCreativeStatus.WITH_ISSUES && // has no issues
    !hasImproperCTA(creative, optimization) && // doesn't have config issues with campaign optimization
    isValidCreative(creative)

  const selectCreative = (newCreative: AdCreative) => {
    const alreadySelected = selectedCreatives.some(c => c.id === newCreative.id)
    if (alreadySelected) {
      onChangeCreatives(selectedCreatives.filter(c => c.id !== newCreative.id))
    } else if (selectedCreatives.length < MAX_SELECTED_CREATIVES) {
      onChangeCreatives([...selectedCreatives, newCreative])
    } else {
      // Previously this branch was missing entirely, so taps past the cap silently no-opped.
      toast.warn(
        `Facebook allows ${MAX_SELECTED_CREATIVES} ads per ad set, so you can't select more than ${MAX_SELECTED_CREATIVES} creatives. Deselect one to swap it out.`
      )
    }
  }

  const selectAll = (visibleCreatives: AdCreative[]) => {
    const selectedIds = new Set(selectedCreatives.map(c => c.id))
    const additions = visibleCreatives.filter(c => !selectedIds.has(c.id))
    const remainingSlots = MAX_SELECTED_CREATIVES - selectedCreatives.length
    if (remainingSlots <= 0 || additions.length === 0) return

    const toAdd = additions.slice(0, remainingSlots)
    onChangeCreatives([...selectedCreatives, ...toAdd])

    if (additions.length > toAdd.length) {
      toast.warn(
        `Facebook allows ${MAX_SELECTED_CREATIVES} ads per ad set, so only the first ${toAdd.length} were selected.`
      )
    }
  }

  const deselectAll = (visibleCreatives: AdCreative[]) => {
    const visibleIds = new Set(visibleCreatives.map(c => c.id))
    onChangeCreatives(selectedCreatives.filter(c => !visibleIds.has(c.id)))
  }

  return (
    <AdCreativeList
      creatives={creatives}
      selectedCreatives={selectedCreatives}
      selectCreative={selectCreative}
      onSelectAll={selectAll}
      onDeselectAll={deselectAll}
      withholdSelect={withholdRejected}
      onRemoveWithheld={deselectAll}
      reviewHref={reviewHref}
      toolbarExtra={toolbarExtra}
      maxSelectable={MAX_SELECTED_CREATIVES}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isFetching={isFetching}
    />
  )
}
